import { useCallback, useEffect, useMemo, useState } from "react";

import Icon from "@/components/icon/icon";
import styles from "@/features/widget-forms/forms/weather-model-map/weather-model-map-dialog.module.css";
import type { ObservationsMapParameterDisplayGroup } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";

import {
  displayGroupsEqual,
  getTimeSeriesParameterEntries,
  insertSingletonDisplayGroupFromPool,
  joinParameterToDisplayGroup,
  mergeDisplayGroups,
  removeParameterFromDisplayGroup,
  reorderDisplayGroupParameters,
  resolveDisplayGroupLabel,
  syncDisplayGroupsFromTimeSeries,
} from "./display-group-board-sync";
import { DisplayGroupBoard } from "./display-group-board";
import { DisplayGroupParameterPool } from "./display-group-parameter-pool";
import { findDisplayGroupFormIndex } from "./display-group-form.utils";
import sectionStyles from "./observations-map-display-group-section.module.css";
import { useObservationsMapForm } from "./use-observations-map-form";

type ObservationsMapDisplayGroupSectionProps = {
  showSectionTitle?: boolean;
};

export function ObservationsMapDisplayGroupSection({
  showSectionTitle = false,
}: ObservationsMapDisplayGroupSectionProps) {
  const { t } = useTranslation();
  const {
    parameters,
    timeSeriesConfigurations,
    displayGroups,
    setDisplayGroups,
    reorderDisplayGroups,
    updateDisplayGroup,
    removeDisplayGroup,
    ensureParameterInTimeSeries,
  } = useObservationsMapForm();

  const [parametersPanelGroupId, setParametersPanelGroupId] = useState<string | null>(null);

  const poolEntries = useMemo(
    () => getTimeSeriesParameterEntries(timeSeriesConfigurations),
    [timeSeriesConfigurations],
  );

  const catalogLabel = useCallback(
    (parameterId: string) => parameters?.find((p) => p.id === parameterId)?.label,
    [parameters],
  );

  const syncedGroups = useMemo(
    () => syncDisplayGroupsFromTimeSeries(displayGroups, timeSeriesConfigurations),
    [displayGroups, timeSeriesConfigurations],
  );

  useEffect(() => {
    if (!displayGroupsEqual(syncedGroups, displayGroups)) {
      setDisplayGroups(syncedGroups);
    }
  }, [syncedGroups, displayGroups, setDisplayGroups]);

  useEffect(() => {
    if (parametersPanelGroupId === null) return;
    const group = displayGroups.find((entry) => entry.id === parametersPanelGroupId);
    if (!group || group.parameter_ids.length <= 1) {
      setParametersPanelGroupId(null);
    }
  }, [displayGroups, parametersPanelGroupId]);

  const usedParameterIds = useMemo(
    () => new Set(displayGroups.flatMap((group) => group.parameter_ids)),
    [displayGroups],
  );

  const handleCombine = useCallback(
    (fromIndex: number, toIndex: number) => {
      setDisplayGroups(mergeDisplayGroups(displayGroups, fromIndex, toIndex));
    },
    [displayGroups, setDisplayGroups],
  );

  const handleRename = useCallback(
    (index: number, labelOverride: string | null) => {
      const group = displayGroups[index];
      if (!group) return;
      updateDisplayGroup(index, { ...group, label_override: labelOverride });
    },
    [displayGroups, updateDisplayGroup],
  );

  const handleJoinFromPool = useCallback(
    (groupIndex: number, parameterId: string) => {
      setDisplayGroups(joinParameterToDisplayGroup(displayGroups, groupIndex, parameterId));
    },
    [displayGroups, setDisplayGroups],
  );

  const handleInsertFromPool = useCallback(
    (parameterId: string, atIndex: number) => {
      setDisplayGroups(
        insertSingletonDisplayGroupFromPool(
          displayGroups,
          parameterId,
          atIndex,
          timeSeriesConfigurations,
          poolEntries,
        ),
      );
    },
    [displayGroups, setDisplayGroups, timeSeriesConfigurations, poolEntries],
  );

  const handleRemove = useCallback(
    (index: number) => {
      removeDisplayGroup(index);
    },
    [removeDisplayGroup],
  );

  const handleReorderParameters = useCallback(
    (groupId: string, fromIndex: number, toIndex: number) => {
      const groupIndex = findDisplayGroupFormIndex(displayGroups, groupId);
      if (groupIndex === -1) return;
      setDisplayGroups(
        reorderDisplayGroupParameters(displayGroups, groupIndex, fromIndex, toIndex),
      );
    },
    [displayGroups, setDisplayGroups],
  );

  const handleRemoveParameter = useCallback(
    (groupId: string, parameterId: string) => {
      const groupIndex = findDisplayGroupFormIndex(displayGroups, groupId);
      if (groupIndex === -1) return;
      setDisplayGroups(
        removeParameterFromDisplayGroup(displayGroups, groupIndex, parameterId),
      );
    },
    [displayGroups, setDisplayGroups],
  );

  const handleParametersPanelOpenChange = useCallback((groupId: string, open: boolean) => {
    if (open) {
      setParametersPanelGroupId(groupId);
      return;
    }
    setParametersPanelGroupId((current) => (current === groupId ? null : current));
  }, []);

  return (
    <div className={`${styles.settingsPanel} ${sectionStyles.fillPanel}`} data-split-layout>
      <div className={sectionStyles.mainRegion}>
        {showSectionTitle && (
          <div className={sectionStyles.sectionTitleBlock}>
            <h3 className={sectionStyles.sectionTitle}>
              {t("admin.widgets.fields.observations_map.marker_designs.section_title")}
            </h3>
            <p className={sectionStyles.sectionSubtitle}>
              {t("admin.widgets.fields.observations_map.marker_designs.board_subtitle")}
            </p>
          </div>
        )}
        <p className={sectionStyles.boardHint}>
          {t("admin.widgets.fields.observations_map.marker_designs.board_hint")}
        </p>
        <DisplayGroupBoard
          groups={displayGroups}
          parameters={parameters ?? []}
          poolEntries={poolEntries}
          timeSeriesEntries={poolEntries}
          timeSeriesConfigurations={timeSeriesConfigurations}
          catalogLabel={catalogLabel}
          onFixParameter={ensureParameterInTimeSeries}
          onReorder={reorderDisplayGroups}
          onCombine={handleCombine}
          onRename={handleRename}
          onJoinFromPool={handleJoinFromPool}
          onInsertFromPool={handleInsertFromPool}
          onRemove={handleRemove}
          parametersPanelGroupId={parametersPanelGroupId}
          onParametersPanelOpenChange={handleParametersPanelOpenChange}
          onReorderParameters={handleReorderParameters}
          onRemoveParameter={handleRemoveParameter}
          emptyState={
            <div className={sectionStyles.emptyStateContent}>
              <Icon name="map-pin" size={32} />
              <span className={sectionStyles.emptyStateText}>
                {t("admin.widgets.fields.observations_map.marker_designs.board_empty")}
              </span>
            </div>
          }
        />
      </div>
      <hr className={sectionStyles.divider} />
      <div className={sectionStyles.poolRegion}>
        <DisplayGroupParameterPool
          entries={poolEntries}
          parameters={parameters ?? []}
          timeSeriesConfigurations={timeSeriesConfigurations}
          usedParameterIds={usedParameterIds}
        />
      </div>
    </div>
  );
}

export function resolveDisplayGroupLabelsForPreview(
  groups: ObservationsMapParameterDisplayGroup[],
  poolEntries: ReturnType<typeof getTimeSeriesParameterEntries>,
  catalogLabel: (id: string) => string | undefined,
): string[] {
  return groups.map((group) => resolveDisplayGroupLabel(group, poolEntries, catalogLabel));
}
